import { useApp } from '@/context/AppContext';
import ChemBar from '@/components/ui/ChemBar';
import { formatDistance } from '@/hooks/useGeofencing';

export default function ActiveStop() {
  const { activeStop, custById, completeStop, setMobileScreen, openModal, geo, hasPlugin, stops } = useApp();

  if (!activeStop) {
    return (
      <div className="mobile-page">
        <div style={{ textAlign: 'center', color: 'var(--t3)', padding: 40 }}>No active stop</div>
      </div>
    );
  }

  const c = custById(activeStop.custId);
  if (!c) return null;

  const handleComplete = () => {
    // Determine whether there's a next stop before dispatching (so the prev
    // stop's tech/date is still readable from the activeStop snapshot).
    const nextStop = stops
      .filter(s =>
        s.id !== activeStop.id &&
        s.tech === activeStop.tech &&
        s.date === activeStop.date &&
        (s.status === 'pending' || s.status === 'active')
      )
      .sort((a, b) => (a.order || 0) - (b.order || 0))[0];

    completeStop(activeStop.id);

    if (nextStop) {
      // completeStop already updates activeStopId, but we ensure we stay on
      // the stop screen to walk the next one.
      setMobileScreen('stop');
    } else {
      setMobileScreen('day');
    }
  };

  return (
    <div className="mobile-page">
      {/* Hero header */}
      <div
        className="mobile-hero"
        style={{ margin: '-14px -16px 0', padding: '14px 16px 16px' }}
      >
        <button className="m-back" onClick={() => setMobileScreen('day')}>
          ← Back to route
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div className="m-stop-num sn-active" style={{ width: 30, height: 30, fontSize: 13 }}>
            {activeStop.order}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t1)', letterSpacing: '-.3px' }}>{c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>{c.addr}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {c.gateCode && (
            <span style={{ padding: '3px 9px', borderRadius: 6, background: 'var(--s3)', fontSize: 11, color: 'var(--t1)' }}>
              Gate: {c.gateCode}
            </span>
          )}
          {c.pets && c.pets !== 'None' && (
            <span style={{ padding: '3px 9px', borderRadius: 6, background: 'var(--s3)', fontSize: 11, color: 'var(--amber)' }}>
              {c.pets}
            </span>
          )}
          <span style={{ padding: '3px 9px', borderRadius: 6, background: 'var(--s3)', fontSize: 11, color: 'var(--t2)' }}>
            {c.poolGal} gal
          </span>
          {hasPlugin('geofencing') && geo?.distances?.get?.(activeStop.id) != null && (
            <span style={{
              padding: '3px 9px',
              borderRadius: 6,
              background: geo.inside?.has?.(activeStop.id) ? 'var(--teal-t)' : 'var(--s3)',
              fontSize: 11,
              color: geo.inside?.has?.(activeStop.id) ? 'var(--teal)' : 'var(--cyan)',
              fontWeight: 700,
            }}>
              📍 {geo.inside?.has?.(activeStop.id) ? 'On site' : `${formatDistance(geo.distances.get(activeStop.id))} away`}
            </span>
          )}
        </div>
      </div>

      {/* Notes */}
      {c.notes && (
        <div style={{
          padding: '10px 12px',
          background: 'var(--s2)',
          borderRadius: 10,
          border: '1px solid var(--b1)',
          fontSize: 12.5,
          color: 'var(--t2)',
          lineHeight: 1.55,
        }}>
          {c.notes}
        </div>
      )}

      {/* Last chemistry */}
      <div className="m-card">
        <div style={{
          padding: '9px 14px',
          borderBottom: '1px solid var(--b1)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--t3)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
        }}>
          Last Chemistry
        </div>
        {c.chem ? (
          <div style={{ padding: '4px 14px 8px' }}>
            <ChemBar param="cl"  label="Chlorine"   value={c.chem.cl} />
            <ChemBar param="ph"  label="pH"          value={c.chem.ph} />
            <ChemBar param="alk" label="Alkalinity"  value={c.chem.alk} />
            <ChemBar param="cya" label="CYA"         value={c.chem.cya} />
          </div>
        ) : (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--t3)', fontSize: 12.5 }}>
            No readings yet — log chemistry below
          </div>
        )}
      </div>

      {/* Action buttons grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button className="m-action" onClick={() => setMobileScreen('chem')}>
          <div className="m-action-icon">🧪</div>
          <div className="m-action-label">Log Chem</div>
        </button>
        <button className="m-action" onClick={() => setMobileScreen('photos')}>
          <div className="m-action-icon">📸</div>
          <div className="m-action-label">Photos</div>
        </button>
        <button className="m-action" onClick={() => openModal('work_order', { stopId: activeStop.id })}>
          <div className="m-action-icon">🔧</div>
          <div className="m-action-label">Work Order</div>
        </button>
        <button className="m-action" onClick={() => openModal('send_message', { custId: c.id })}>
          <div className="m-action-icon">💬</div>
          <div className="m-action-label">Message</div>
        </button>
        <button
          className="m-action"
          style={{ gridColumn: '1 / -1' }}
          onClick={() => openModal('extra_charge', { stopId: activeStop.id, custId: c.id })}
        >
          <div className="m-action-icon">💵</div>
          <div className="m-action-label">+ Extra Charge</div>
        </button>
      </div>

      {/* Complete button */}
      <button
        className="m-action primary"
        style={{ width: '100%' }}
        onClick={handleComplete}
      >
        <div className="m-action-icon">✅</div>
        <div className="m-action-label" style={{ fontSize: 14 }}>Mark Stop Complete</div>
      </button>
    </div>
  );
}
